import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { ISlimApp, ResourceType } from '@biolibtech/biolib-js';
import { Link } from 'gatsby';
import config from '../../config';
import { stripMarkdown } from '../../utils/stripMarkdown';
import clsx from 'clsx';
import ProfilePicture from './ProfilePicture';

export interface IListItemContextData {
    isAccountHandleShown: boolean;
    leftIcon?: React.ReactElement;
    disableBorder?: boolean;
    showProfilePicture?: boolean;
    disableLinks?: boolean;
    hideChevron?: boolean;
    compact?: boolean;
}

interface IProps {
    style?: React.CSSProperties;
    item: ISlimApp | null;
    contextData: IListItemContextData;
    asLink?: boolean;
    className?: string;
}

function ResourceListItemContent(props: { resource: ISlimApp; contextData: IListItemContextData }) {
    const { resource, contextData } = props;

    return (
        <>
            {contextData.leftIcon && <div className='flex-shrink-0'>{contextData.leftIcon}</div>}

            {contextData.showProfilePicture && (
                <div className='flex-shrink-0'>
                    <ProfilePicture
                        src={
                            resource.account_profile_picture_200x200 ??
                            resource.account_profile_picture ??
                            config.placeholderProfileImage
                        }
                        accountHandle={resource.account_handle}
                        className='size-9 rounded bg-slate-50 flex-shrink-0 border border-slate-100 mt-1'
                    />
                </div>
            )}

            {/* Left side */}
            <div className='flex-1 w-full relative mb-5 min-w-0'>
                <p className={clsx('text-gray-600 group-data-[focus]:text-white flex items-center max-w-full', contextData.compact ? 'text-sm' : 'text-base')}>
                    <span className='truncate min-w-0'>
                        {contextData.isAccountHandleShown && <span>{resource.account_handle}/</span>}
                        <span className='font-semibold text-gray-900 group-data-[focus]:text-white'>{resource.name}</span>
                    </span>
                    {(resource.is_unlisted || resource.state === 'draft') && (
                        <span className='ml-2 inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-800 ring-1 ring-inset ring-gray-500/10 flex-shrink-0'>
                            {resource.is_unlisted ? 'Unlisted' : 'Private'}
                        </span>
                    )}
                    {resource.is_archived && (
                        <span className='ml-2 inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-800 ring-1 ring-inset ring-gray-500/10 flex-shrink-0'>
                            Archived
                        </span>
                    )}
                </p>
                <p className={clsx('absolute w-full text-gray-600 group-data-[focus]:text-indigo-100 truncate', contextData.compact ? 'text-xs' : 'text-sm')}>
                    {stripMarkdown((resource.description || resource.active_version_description || '').slice(0, 200))}
                </p>
            </div>

            {/* Right side */}
            {!contextData.hideChevron && (
                <div className='flex-none sm:w-42 flex items-center gap-x-1'>
                    <ChevronRightIcon
                        aria-hidden='true'
                        className='size-5 flex-none text-gray-400 group-data-[focus]:text-white'
                    />
                </div>
            )}
        </>
    );
}

export default function ResourceListItem(props: IProps) {
    const { style, item: resource, contextData, asLink = !contextData.disableLinks, className } = props;

    const wrapperClassname = clsx(
        'h-16 flex flex-row overflow-hidden items-center justify-between py-0 gap-x-3 hover:no-underline',
        { 'cursor-pointer': asLink },
        { 'border-b border-gray-100': !contextData.disableBorder },
        className ?? 'px-6',
    );

    if (!resource) {
        return (
            <div style={style} className={wrapperClassname}>
                {contextData.showProfilePicture && (
                    <div className='size-9 rounded bg-slate-50 flex-shrink-0 border border-slate-100 mt-1' />
                )}

                {/* Left side skeleton */}
                <div className='flex-1 w-full relative'>
                    {/* Title skeleton */}
                    <div className='inline-flex items-center'>
                        <div className='h-5 w-40 bg-gray-200 animate-pulse rounded' />
                        <div className='ml-2 h-4 w-16 bg-gray-200 animate-pulse rounded-full' />
                    </div>
                    {/* Description skeleton */}
                    <div className='mt-1 h-4 w-full bg-gray-200 animate-pulse rounded' />
                </div>

                {/* Right side skeleton */}
                {!contextData.hideChevron && (
                    <div className='flex-none sm:w-42 flex items-center gap-x-1'>
                        <ChevronRightIcon aria-hidden='true' className='size-5 flex-none text-gray-400' />
                    </div>
                )}
            </div>
        );
    }

    const linkTo =
        resource.type === ResourceType.experiment
            ? `/experiments/${resource.uuid}/`
            : `/${config.resourcePrefix}${resource.account_handle}/${resource.name}/`;

    if (!asLink) {
        return (
            <div style={style} className={clsx(wrapperClassname, className ? '' : 'hover:bg-gray-100')}>
                <ResourceListItemContent resource={resource} contextData={contextData} />
            </div>
        );
    }

    return (
        <Link style={style} to={linkTo} className={clsx(wrapperClassname, className ? '' : 'hover:bg-gray-100')}>
            <ResourceListItemContent resource={resource} contextData={contextData} />
        </Link>
    );
}
